import AppShell from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  classKey,
  classLabelFromKey,
  getDefaultFeePlanGroups,
  getFeeInstallmentProgress,
  getSessionUser,
  getStudentRecordForSession,
  isTestPaymentStudent,
} from "@/lib/eisStore";
import { useNavigate } from "react-router-dom";

function StatCard({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-900 border-slate-200",
    blue: "bg-blue-50 text-blue-900 border-blue-200",
    green: "bg-emerald-50 text-emerald-900 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
  };

  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.slate}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-2 text-xl md:text-2xl font-extrabold break-words">{value}</p>
    </div>
  );
}

function StudentFeeView({ user, onProceedToCheckout }) {
  const studentRecord = getStudentRecordForSession(user) || user || {};
  const allowTestPayment = isTestPaymentStudent(user) || isTestPaymentStudent(studentRecord);
  const standard = studentRecord.standard || user?.standard || "";
  const division = studentRecord.division || user?.division || "";
  const targetClassKey = standard && division ? classKey(standard, division) : "";

  if (!targetClassKey) {
    return (
      <Card className="border-slate-200 shadow-md">
        <CardContent className="py-14 text-center">
          <p className="text-xl md:text-2xl font-bold text-slate-800">
            Your class is not set yet.
          </p>
          <p className="mt-2 text-slate-500">
            Please complete your student profile so we can show the correct fee details.
          </p>
        </CardContent>
      </Card>
    );
  }

  const summary = getFeeInstallmentProgress(
    targetClassKey,
    studentRecord.id || user.id || user.email,
    standard,
  );
  const plan = summary.plan || {};
  const nextInstallment = summary.nextInstallment;
  const dueDate = summary.nextDueDate || "Not set";
  const payableAmount = summary.nextAmount || 0;
  const payableLabel =
    summary.isFullyPaid && !allowTestPayment ? "Paid" : `₹${payableAmount.toLocaleString("en-IN")}`;

  return (
    <div className="space-y-8">
      <Card className="overflow-hidden border-slate-200 shadow-md">
        <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle className="text-2xl md:text-3xl text-slate-900">
                {classLabelFromKey(targetClassKey)}
              </CardTitle>
              <p className="mt-1 text-sm text-slate-500">
                Fee summary for {studentRecord.studentName || studentRecord.name || "student"}
              </p>
            </div>
            <Badge className="w-fit bg-[#0b1f7a] text-white hover:bg-[#0b1f7a]">
              Student View
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              label="Payable Amount"
              value={payableLabel}
              tone="blue"
            />
            <StatCard label="Due Date" value={dueDate} tone="amber" />
            <StatCard
              label="Paid in Advance"
              value={`₹${summary.paidAmount.toLocaleString("en-IN")}`}
              tone="green"
            />
            <StatCard
              label="Total Fees"
              value={`₹${Number(plan.totalAmount || 0).toLocaleString("en-IN")}`}
              tone="slate"
            />
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">
              Installments
            </p>
            <div className="space-y-3">
              {(summary.installmentStatuses || plan.installments || []).map((installment) => (
                <div
                  key={installment.label}
                  className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3"
                >
                  <div>
                    <p className="font-semibold text-slate-800">{installment.label}</p>
                    <p className="text-xs text-slate-500">{installment.dueDate}</p>
                    <p className="text-[11px] font-semibold uppercase tracking-wide mt-1 text-slate-500">
                      {installment.status === "paid"
                        ? "Paid"
                        : installment.status === "partial"
                          ? "Partially paid"
                          : installment.status === "due"
                            ? "Due now"
                            : "Upcoming"}
                    </p>
                  </div>
                  <p className="font-bold text-slate-900">
                    ₹{Number(installment.amount || 0).toLocaleString("en-IN")}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <Button
            type="button"
            onClick={onProceedToCheckout}
            disabled={!allowTestPayment && (summary.isFullyPaid || !nextInstallment)}
            className="w-full rounded-full bg-[#0b1f7a] font-semibold text-white hover:bg-[#09185f]"
          >
            {allowTestPayment
              ? "Pay Now"
              : summary.isFullyPaid
                ? "All Fees Paid"
                : `Pay ${nextInstallment?.label || "Next Installment"}`}
          </Button>

          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
            <p className="font-semibold text-slate-800">Payment status</p>
            <p className="mt-1">
              This page now shows only the fee structure for the student&apos;s own class.
              After each payment, the next installment will become due automatically.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ClassWiseFeeView() {
  const feeGroups = getDefaultFeePlanGroups();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Fees</h1>
        <p className="mt-2 text-slate-500 max-w-2xl">
          View the class-wise fee structure. This view is kept for school staff roles.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {feeGroups.map((group) => (
          <Card key={group.title} className="overflow-hidden border-slate-200 shadow-md">
            <CardHeader className="space-y-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-xl md:text-2xl text-slate-900">{group.title}</CardTitle>
                  <p className="mt-1 text-sm text-slate-500">{group.standards.join(" - ")}</p>
                </div>
                <Badge className="bg-[#0b1f7a] hover:bg-[#0b1f7a] text-white">
                  {group.standards.length} classes
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-5 p-6">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Annual Fee
                </p>
                <p className="mt-2 text-3xl font-extrabold text-[#0b1f7a]">
                  ₹{group.totalAmount.toLocaleString("en-IN")}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">
                  Installments
                </p>
                <div className="space-y-3">
                  {group.installments.map((installment) => (
                    <div
                      key={installment.label}
                      className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3"
                    >
                      <div>
                        <p className="font-semibold text-slate-800">{installment.label}</p>
                        <p className="text-xs text-slate-500">{installment.dueDate}</p>
                      </div>
                      <p className="font-bold text-slate-900">
                        ₹{Number(installment.amount || 0).toLocaleString("en-IN")}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type="button"
                disabled
                className="w-full rounded-full bg-[#0b1f7a] font-semibold text-white opacity-70"
              >
                Checkout coming next
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function Fees() {
  const user = getSessionUser();
  const navigate = useNavigate();
  const isStudent = user?.role === "student";
  const studentRecord = isStudent ? getStudentRecordForSession(user) || user || {} : null;
  const standard = studentRecord?.standard || user?.standard || "";
  const division = studentRecord?.division || user?.division || "";
  const targetClassKey = standard && division ? classKey(standard, division) : "";

  const proceedToCheckout = () => {
    navigate("/checkout", {
      state: {
        studentClassKey: targetClassKey,
      },
    });
  };

  return (
    <AppShell>
      <div data-testid="fees-page" className="space-y-8">
        {isStudent ? (
          <StudentFeeView user={user} onProceedToCheckout={proceedToCheckout} />
        ) : (
          <ClassWiseFeeView />
        )}
      </div>
    </AppShell>
  );
}
